import segmentAnalytics from '@segment/analytics.js'
import {isEmpty} from 'ramda'

import {isGoodBot} from './botDetection'
import {AnalyticsSDKType, IntegrationsType} from '../types/AnalyticsSDK'

const analytics = segmentAnalytics as AnalyticsSDKType

const normalizeNullValues = function (metadata: Record<string, unknown>) {
  if (metadata == undefined) metadata = {}

  return Object.keys(metadata).reduce(function (mem, k) {
    let v = metadata[k] as string & Record<string, unknown>
    if (
      (typeof v === 'string' && v === '') ||
      (typeof v === 'object' && isEmpty(v)) ||
      v === undefined
    ) {
      v = null
    }

    mem[k] = v
    return mem
  }, {})
}

/**
 * Analytics helper functions object
 */
const AnalyticsUtils = {
  /** Default for log events */
  logEvents: false,
  /** Set the log events default status */
  setLogEventsStatus: status => {
    AnalyticsUtils.logEvents = status
  },
  // TODO: Write unit tests
  /** Return if analytics is initialized */
  analyticsPlatformEnabled() {
    return Boolean(analytics?.initialized)
  },
  /** Initialized analytics if possible */
  initialize(integrations: IntegrationsType, anonymousId: string) {
    analytics?.initialize?.(integrations)
    analytics?.user?.().anonymousId(anonymousId)
  },
  /**
   * Overrides the destination options for the Segment / EvAS API endpoint.
   * Used for sending events to a different endpoint, for instance while migrating to a new endpoint.
   *
   * @remarks This method modifies internals of the Analytics.js implementation as there is no official API available.
   * @param apiHost - URL of the analytics endpoint (excluding https://)
   * @param apiKey
   */
  overrideSegmentDestinationSettings(apiHost: string, apiKey: string) {
    // @ts-expect-error: Property '_integrations' does not exist on type 'AnalyticsSDKType'.
    analytics._integrations['Segment.io'].options.apiHost = apiHost
    // @ts-expect-error: Property '_integrations' does not exist on type 'AnalyticsSDKType'.
    analytics._integrations['Segment.io'].options.apiKey = apiKey
  },

  /**
   * Track events
   * @param event - event name
   * @param metadata - extra event data
   */
  trackEvent(event: string, metadata: Record<string, unknown>) {
    metadata = normalizeNullValues(metadata)
    if (AnalyticsUtils.logEvents) console.info(`[TRACK] ${event}`, metadata)

    return analytics?.track?.(event, metadata)
  },

  identify: analytics.identify,

  trackPageView: analytics?.page?.bind(analytics, 'Page'),

  user: analytics.user,

  /**
   * Segment persist userId and anonymousId. We need to clean up the userId after the user logout or change,
   * but we need to keep the anonymousId as it is. Calling `analytics.reset` will also update anonymousId,
   * which is not what we want. This function will reset the userId and keep the anonymousId value.
   * @param userId
   */
  resetAnalytics: (userId?: string) => {
    const anonymousId = analytics.user().anonymousId()
    const analyticsUserId = analytics.user().id()
    if (analyticsUserId && userId !== analyticsUserId) {
      analytics.reset()
      analytics.user().anonymousId(anonymousId)
    }
  }
}

export {isGoodBot}
export default AnalyticsUtils
